"use client";

import React from "react";
import "./BestSellers.scss";
import SwiperMainCard from "@/components/Shared/SwiperMainCard/SwiperMainCard";
import { useMediaQuery } from "@mui/material";
import { useRouter } from "next/navigation";
import { ApiResponse, Product } from "@webelocity/core";
import { apiFetch } from "@/lib/shopApi";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { fadeIn, fadeUp, defaultViewport } from "@/utils/animations";
import { ProductCard, ReUsableSwiper } from "@webelocity/ui";

const BEST_SELLER_IMAGE = "/assets/Home/BestSellers/ToolsV2.webp";

const BestSellers = () => {
  const router = useRouter();
  const isMobile = useMediaQuery("(max-width:1023px)");

  const {
    data: productsResponse,
    isLoading,
    isError,
  } = useQuery<ApiResponse<Product>>({
    queryKey: ["BestSellers"],
    queryFn: () =>
      apiFetch<ApiResponse<Product>>(`/products/top-selling`, {
        method: "GET",
        query: { availability: "in_stock" },
      }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  if (isError) {
    return (
      <div className="home-best-sellers baseContainer">
        <div className="maxWidth">
          <div className="home-best-sellers-wrapper">
            <p className="section-main-header black">Best Sellers</p>
            <div className="best-sellers-error">Failed to fetch data</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="home-best-sellers baseContainer" style={{ position: "relative" }}>
      <div className="maxWidth">
        <div className="home-best-sellers-wrapper">
        <motion.p
          className="section-main-header black"
          variants={fadeIn}
          initial="hidden"
          whileInView="visible"
          viewport={defaultViewport}
        >
          Best Sellers
        </motion.p>

        <motion.div
          className="best-sellers-content"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={defaultViewport}
        >
          {isMobile ? (
            <>
              <div className="best-sellers-hero-mobile">
                <SwiperMainCard
                  title="SELLER PRODUCTS"
                  span="BEST"
                  image={BEST_SELLER_IMAGE}
                  header=""
                />
              </div>
              <div className="best-sellers-swiper-mobile">
                <ReUsableSwiper<Product>
                  data={productsResponse?.data ?? []}
                  renderSlide={(product) => <ProductCard product={product} />}
                  isLoading={isLoading}
                  isError={false}
                  error={undefined}
                  skeletonCount={4}
                  swiperOptions={{
                    slidesPerView: 1,
                    spaceBetween: 16,
                    breakpoints: {
                      768: { slidesPerView: 2 },
                      1200: { slidesPerView: 3 },
                    },
                  }}
                  className="home-best-sellers-swiper"
                  slideStyles="!h-auto"
                />
              </div>
            </>
          ) : (
            <div className="best-sellers-row">
              <div className="best-sellers-hero-desktop">
                <SwiperMainCard
                  title="SELLER PRODUCTS"
                  span="BEST"
                  image={BEST_SELLER_IMAGE}
                  header=""
                />
              </div>
              <div className="best-sellers-swiper-desktop">
                <ReUsableSwiper<Product>
                  data={productsResponse?.data ?? []}
                  renderSlide={(product) => <ProductCard product={product} />}
                  isLoading={isLoading}
                  isError={false}
                  error={undefined}
                  skeletonCount={6}
                  swiperOptions={{
                    slidesPerView: 3,
                    spaceBetween: 16,
                    breakpoints: {
                      768: { slidesPerView: 2 },
                      1200: { slidesPerView: 3 },
                    },
                  }}
                  className="home-best-sellers-swiper"
                  slideStyles="!h-auto"
                />
              </div>
            </div>
          )}
        </motion.div>

        {!isLoading && !isError && (productsResponse?.data?.length ?? 0) > 0 && (
          <motion.div
            className="bottom-wrapper"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={defaultViewport}
          >
            <button
              className="main-btn white"
              onClick={() => router.push("/shop/catalogue?sort=best_selling")}
            >
              See More
            </button>
          </motion.div>
        )}
        </div>
      </div>
    </div>
  );
};

export default BestSellers;
